import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Download, BookOpen, ListChecks, FileText, Loader2, History,
  ClipboardList, ArrowRight,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

// -----------------------------------------------------------------------
//  Sprint A.1.3-β — Preview Dialog (markdown HTML for docs / safe Q-preview
//  for sample_questionnaires) + Download (PDF/DOCX/blob) + Import-to-library
// -----------------------------------------------------------------------

export function ResourcePreviewDialog({ open, onOpenChange, resource, caps, onChanged }) {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lang, setLang] = useState(() => (i18n?.language || 'en').startsWith('es') ? 'es' : 'en');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !resource?.id) return;
    setLoading(true);
    adminApi.previewResource(resource.id, lang)
      .then(setData)
      .catch((e) => toast.error(t('resourceCenter.previewDialog.previewFailed', { defaultValue: 'Preview failed' }), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [open, resource?.id, lang]);

  const isSample = resource?.category === 'sample_questionnaires';
  const isTemplate = resource?.category === 'templates';
  const isDoc = !isSample;
  const hasPayload = !!(data?.questionnaire_preview?.question_count > 0);
  const canImport = (isSample || isTemplate) && caps?.can_upload && hasPayload;

  async function handleDownload(format) {
    try {
      const blob = await adminApi.exportResource(resource.id, format, lang);
      const url = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resource.title.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 60)}_${lang}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('resourceCenter.previewDialog.downloadedToast', { defaultValue: 'Downloaded as {{format}} ({{lang}})', format: format.toUpperCase(), lang: lang.toUpperCase() }));
      onChanged?.();
    } catch (e) {
      toast.error(t('resourceCenter.previewDialog.downloadFailed', { defaultValue: 'Download failed' }), { description: formatApiError(e) });
    }
  }

  async function handleImport() {
    setImporting(true);
    try {
      const res = await adminApi.importResourceToLibrary(resource.id);
      toast.success(t('resourceCenter.previewDialog.importCreatedToast', { defaultValue: 'Questionnaire draft created' }), {
        description: t('resourceCenter.previewDialog.importOpening', { defaultValue: 'Opening it in the Evaluation Library…' }),
      });
      onChanged?.();
      onOpenChange(false);
      setTimeout(() => navigate(`/admin/evaluation-library/${res.questionnaire_id}`), 200);
    } catch (e) {
      toast.error(t('resourceCenter.previewDialog.importFailed', { defaultValue: 'Import failed' }), { description: formatApiError(e) });
    } finally {
      setImporting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col" data-testid="resource-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSample ? <ListChecks className="h-5 w-5 text-primary" /> : <BookOpen className="h-5 w-5 text-primary" />}
            <span>{tData(resource?.title)}</span>
            {resource?.version && <Badge variant="outline" className="ml-2">v{resource.version}</Badge>}
          </DialogTitle>
          {resource?.description && (
            <p className="text-sm text-muted-foreground mt-1">{tData(resource.description)}</p>
          )}
          {(data?.available_langs || []).length > 1 && (
            <div className="flex items-center gap-1.5 mt-2" data-testid="resource-preview-lang-toggle">
              <span className="text-xs text-muted-foreground">{t('resourceCenter.previewDialog.language', { defaultValue: 'Language:' })}</span>
              {data.available_langs.map((lc) => (
                <Button
                  key={lc}
                  variant={lang === lc ? 'default' : 'outline'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setLang(lc)}
                  data-testid={`resource-preview-lang-${lc}`}
                >
                  {lc.toUpperCase()}
                </Button>
              ))}
            </div>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('resourceCenter.previewDialog.loading', { defaultValue: 'Loading preview…' })}
            </div>
          )}
          {!loading && data && isDoc && (
            <div
              className="resource-preview-html"
              dangerouslySetInnerHTML={{ __html: data.body_html || `<p class="text-muted-foreground">${t('resourceCenter.previewDialog.noContent', { defaultValue: 'No content yet.' })}</p>` }}
              data-testid="resource-preview-body"
            />
          )}
          {!loading && data && isSample && (
            <SampleQuestionnairePreview preview={data.questionnaire_preview} />
          )}
          {!loading && data && isTemplate && (
            <>
              {data.body_md ? (
                <div
                  className="resource-preview-html"
                  dangerouslySetInnerHTML={{ __html: data.body_html }}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-6">{t('resourceCenter.previewDialog.templateReady', { defaultValue: 'Template ready to download.' })}</p>
              )}
            </>
          )}
        </ScrollArea>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="resource-preview-download-btn">
                <Download className="h-4 w-4 mr-2" /> {t('resourceCenter.download', { defaultValue: 'Download' })}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleDownload('pdf')} data-testid="resource-preview-download-pdf">
                <FileText className="h-4 w-4 mr-2" /> {t('resourceCenter.previewDialog.downloadPdf', { defaultValue: 'Download as PDF' })}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('docx')} data-testid="resource-preview-download-docx">
                <FileText className="h-4 w-4 mr-2" /> {t('resourceCenter.previewDialog.downloadDocx', { defaultValue: 'Download as Word (.docx)' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canImport && (
            <Button onClick={handleImport} disabled={importing} data-testid="resource-preview-import-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              {t('resourceCenter.importToLibrary', { defaultValue: 'Import to My Library' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- inner pieces -----------------------------------------------------

function SampleQuestionnairePreview({ preview }) {
  const { t } = useTranslation();
  if (!preview || preview.question_count === 0) {
    return <p className="text-sm text-muted-foreground py-6">{t('resourceCenter.previewDialog.noPayload', { defaultValue: 'No questionnaire payload to preview.' })}</p>;
  }
  const sectionsById = Object.fromEntries((preview.sections || []).map((s) => [s.id, s]));
  const grouped = {};
  (preview.questions || []).forEach((q) => {
    const sid = q.section_id || '__none';
    (grouped[sid] = grouped[sid] || []).push(q);
  });

  return (
    <div className="space-y-4 py-2">
      {preview.objective && (
        <div className="rounded-lg border bg-muted/30 p-3" data-testid="resource-preview-objective">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('resourceCenter.previewDialog.objective', { defaultValue: 'Objective' })}</div>
          <div className="text-sm mt-1">{preview.objective}</div>
        </div>
      )}
      {preview.instructions && (
        <div className="rounded-lg border bg-muted/30 p-3" data-testid="resource-preview-instructions">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('resourceCenter.previewDialog.instructions', { defaultValue: 'Instructions' })}</div>
          <div className="text-sm mt-1">{preview.instructions}</div>
        </div>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{t('resourceCenter.previewDialog.sectionsCount', { defaultValue: '{{count}} sections', count: preview.section_count })}</span>
        <span>•</span>
        <span>{t('resourceCenter.previewDialog.questionsCount', { defaultValue: '{{count}} questions', count: preview.question_count })}</span>
        <span>•</span>
        <span>{t('resourceCenter.previewDialog.bandsCount', { defaultValue: '{{count}} interpretation bands', count: (preview.interpretation_bands || []).length })}</span>
      </div>
      <Separator />
      {(preview.sections || []).map((sec, idx) => (
        <div key={sec.id || `sec-${idx}`} className="space-y-2" data-testid={`resource-preview-section-${idx}`}>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
            <h4 className="text-sm font-semibold">{sec.name}</h4>
            <span className="text-xs text-muted-foreground">{t('resourceCenter.previewDialog.weight', { defaultValue: 'weight {{w}}', w: sec.weight })}</span>
          </div>
          {(grouped[sec.id] || []).map((q, qi) => (
            <div key={q.id || `q-${idx}-${qi}`} className="rounded-md border p-3 ml-7" data-testid={`resource-preview-q-${idx}-${qi}`}>
              <div className="text-sm font-medium">{qi + 1}. {q.prompt}</div>
              <div className="text-xs text-muted-foreground mt-1">{q.type} · {t('resourceCenter.previewDialog.weightWord', { defaultValue: 'weight' })} {q.required ? t('resourceCenter.previewDialog.required', { defaultValue: '(required)' }) : t('resourceCenter.previewDialog.optional', { defaultValue: '(optional)' })}</div>
              {(q.options || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {q.options.map((o) => (
                    <li key={o.id} className="text-sm flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/40" />
                      {o.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
//  Version History Dialog
// -----------------------------------------------------------------------

export function ResourceVersionHistoryDialog({ open, onOpenChange, resource }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !resource?.id) return;
    setLoading(true);
    adminApi.listResourceVersions(resource.id)
      .then((d) => setItems(d.versions || []))
      .catch((e) => toast.error(t('resourceCenter.versionsDialog.failed', { defaultValue: 'Version history failed' }), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [open, resource?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="resource-versions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> {t('resourceCenter.versionHistory', { defaultValue: 'Version history' })} — {tData(resource?.title)}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          {loading && <p className="text-sm text-muted-foreground py-6">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-muted-foreground py-6">
              {t('resourceCenter.versionsDialog.none', { defaultValue: 'No version snapshots yet. Snapshots are created on edit.' })}
            </p>
          )}
          {items.map((v, idx) => (
            <div key={v.id || `version-${idx}`} className="border-l-2 border-primary/30 pl-4 py-3 mb-3" data-testid={`resource-version-row-${v.id || idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">v{v.version}</span>
                <Badge variant="outline" className="text-xs">{v.status}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{new Date(v.at).toLocaleString(getUiLocale())}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {v.file_name ? `file: ${v.file_name} (${v.file_size} bytes)` : ''}
                {v.body_md_len ? ` · body: ${v.body_md_len} chars` : ''}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
//  Changelog Dialog
// -----------------------------------------------------------------------

export function ResourceChangelogDialog({ open, onOpenChange, resource }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const entries = [...(resource?.changelog || [])].reverse();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="resource-changelog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> {t('resourceCenter.changelog', { defaultValue: 'Changelog' })} — {tData(resource?.title)}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6 space-y-3">
          {entries.length === 0 && (
            <p className="text-sm text-muted-foreground py-6">{t('resourceCenter.changelogDialog.none', { defaultValue: 'No changelog entries yet.' })}</p>
          )}
          {entries.map((c, idx) => (
            <div key={c.id || `cl-${idx}`} className="border-l-2 border-muted-foreground/30 pl-4 py-2" data-testid={`resource-changelog-row-${c.id || idx}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{c.action}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(c.at).toLocaleString(getUiLocale())}</span>
              </div>
              <div className="text-sm mt-1">{c.notes || <span className="text-muted-foreground italic">{t('resourceCenter.changelogDialog.noNotes', { defaultValue: 'no notes' })}</span>}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.actor_name || 'system'} · {c.actor_role || '—'}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
